import React, { useContext } from "react";
import { FieldPath, FieldValues } from "react-hook-form";
import { GroupBase } from "react-select";
import {
  ReactSelectSyncProps,
  ReactSelectAsyncProps,
  ReactSelectController,
  ReactSelectControllerProps,
} from "@pushpress/shared-components";
import { AppContext } from "../../../context/app";
import { joinStrings } from "../../../utils/misc";
import { sortByFirstName } from "../../../utils/sortUtils";
import {
  MemberProfileFragment,
  useGetMembersProfilesLazyQuery,
} from "./MemberProfileSelect-generated.hooks";

type MemberProfileControllerProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
  Option extends MemberProfileFragment,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
> = ReactSelectControllerProps<TFieldValues, TName, Option, IsMulti, Group>;

function MemberProfileController<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
  Option extends MemberProfileFragment,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(
  props: MemberProfileControllerProps<
    TFieldValues,
    TName,
    Option,
    IsMulti,
    Group
  > &
    (ReactSelectSyncProps | ReactSelectAsyncProps) & {
      noPortal?: boolean;
    },
) {
  const { noPortal, menuPortalTarget, ...other } = props;

  const app = useContext(AppContext);

  const [getMemberProfiles, { loading }] = useGetMembersProfilesLazyQuery();

  const handleGetProfiles = async (
    value: string,
  ): Promise<MemberProfileFragment[]> => {
    const { data } = await getMemberProfiles({
      variables: {
        getProfilesInput: {
          name: "",
          search: value,
          clientUuid: app.client.uuid,
          isStaff: undefined,
          membershipStatus: undefined,
        },
      },
    });
    return [...(data?.getProfiles || [])].sort(sortByFirstName);
  };

  const getOptionLabel = (option: MemberProfileFragment) =>
    joinStrings(option.firstName, option.lastName);

  const getOptionValue = (option: MemberProfileFragment) => option.userUuid;
  return (
    <ReactSelectController
      {...(!noPortal && {
        menuPortalTarget: menuPortalTarget || document.body,
      })}
      {...other}
      async
      isLoading={loading}
      loadOptions={handleGetProfiles as any}
      getOptionLabel={getOptionLabel}
      getOptionValue={getOptionValue}
    />
  );
}

export default MemberProfileController;
